import {AfterViewChecked, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {StateConfigService} from "../../../services/state/state-config.service";
import {
  faBadgeCheck,
  faCheck,
  faChevronDown,
  faChevronRight,
  faCopy,
  faFile,
  faFilePdf,
  faFolderOpen,
  faMemoCircleCheck,
  faRedo,
  faThumbsDown,
  faThumbsUp,
  faTriangleExclamation,
  faX
} from "@fortawesome/pro-solid-svg-icons";
import {faChatbicAi} from "../../util/icons/custom-icons";

import {PopoutContentService} from "../../../services/popout/popout.service";
import {ChatService, ChatSurface, Message, UiBlock} from "../service/chat.service";
import {initFlowbite, Tooltip} from "flowbite";
import {DatePipe, NgClass} from '@angular/common';
import {MarkdownComponent, MarkdownModule} from 'ngx-markdown';
import {collapseLongLists} from '../util/collapse-long-lists';
import {addTableCsvExport} from '../util/table-csv-export';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {MessageViewComponent} from '../message-view/message-view.component';
import {AvatarComponent} from "../../util/design/avatar/avatar.component";
import {BicBadgeComponent, BicButtonComponent} from "@buildinginformationcloud/bic-ui-components";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {UiBlockPanelComponent} from '../ui-block-panel/ui-block-panel.component';
import {Subscription} from 'rxjs';
import {normalizeMarkdown} from '../../../pipes/normalize-markdown.pipe';
import {messageCopyText} from '../util/message-copy-text';
import {ChatToolStep} from '../../../services/chat/chat-stream.model';
import {ChatLinkService} from '../../../services/chat/chat-link.service';
import {ProposalReviewService} from '../../../services/chat/proposal-review.service';
import {ProposalReviewBarComponent} from '../proposal-review-bar/proposal-review-bar.component';

/**
 * Eine Zeile der Schritt-Anzeige. Fasst aufeinanderfolgende Aufrufe desselben Werkzeugs zusammen;
 * {@link count} ist 1, solange nichts zusammengefasst wurde.
 */
interface GroupedToolStep {
  /** Die stepId des ERSTEN Schrittes der Gruppe – stabiler Schlüssel für `track`. */
  key: string;
  label: string;
  status: ChatToolStep['status'];
  detail?: string;
  count: number;
}

@Component({
  selector: 'app-chatBIC-view',
    templateUrl: './chat-view.component.html',
    styleUrls: ['./chat-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MarkdownModule,
    FaIconComponent,
    NgClass,
    CdkCopyToClipboard,
    MessageViewComponent,
    DatePipe,
    AvatarComponent,
    BicBadgeComponent,
    UiBlockPanelComponent,
    BicButtonComponent,
    ProposalReviewBarComponent
  ]
})
export class ChatViewComponent implements OnInit, AfterViewChecked, OnDestroy {
  protected chatService = inject(ChatService);
  private popOutService = inject(PopoutContentService);
  private chatLinkService = inject(ChatLinkService);
  private stateConfigService = inject(StateConfigService);
  protected proposalReview = inject(ProposalReviewService);

  @ViewChild('chatViewContainer') private chatViewContainer!: ElementRef;
  @ViewChild('questionAnchorSpacer') private questionAnchorSpacer?: ElementRef<HTMLElement>;

  /**
   * Welche Chatfläche diese Ansicht rendert.
   *
   * <p>Der eingebettete myCheck-Chat und der klassische Chat benutzen dieselben Komponenten. Ohne
   * diese Angabe lasen beide denselben Zustand – die myCheck-Konversation erschien im klassischen
   * Chat, und beide teilten sich eine Denk-Sperre.
   */
  @Input() surface: ChatSurface = 'classic';

  private scrollSubscription?: Subscription;
  /**
   * Folgt die Ansicht dem wachsenden Antworttext?
   *
   * <p>Standard ist ja. Scrollt der Nutzer waehrend des Streams nach oben, um etwas nachzulesen,
   * wird daraus nein – sonst risse ihn jedes Token zurueck ans Ende. Scrollt er wieder ans Ende,
   * folgt die Ansicht erneut.
   */
  private stickToBottom = true;

  /** Der Container, auf dem der Scroll-Listener haengt – gemerkt, um ihn wieder abzumelden. */
  private scrollListenerTarget?: HTMLElement;

  /**
   * Ab welchem Abstand zum Ende „der Nutzer liest weiter oben" gilt.
   *
   * <p>Nicht null: waehrend des Streamens waechst der Inhalt zwischen zwei Bildaufbauten, und ein
   * Bruchteil eines Pixels Abweichung wuerde das Mitlaufen sonst grundlos abschalten.
   */
  private static readonly STICK_TO_BOTTOM_THRESHOLD_PX = 64;
  /** Abstand der verankerten letzten Frage zum oberen Rand des Scrollbereichs. */
  private static readonly QUESTION_ANCHOR_TOP_OFFSET_PX = 24;

  private previousMessageCount = 0;
  private previousChatId?: string;
  private previousLastMessageId?: string;

  /** Der Zustand der Fläche, die diese Ansicht rendert. */
  get session() {
    return this.chatService.session(this.surface);
  }

  /** Ob beim letzten Durchlauf eine Antwort gestreamt wurde – siehe {@link collapseListsAfterStreaming}. */
  private wasStreaming = false;

  ngAfterViewChecked() {
    this.collapseListsAfterStreaming();
    const currentChatId = this.session.currentChat?.id;
    if (currentChatId !== this.previousChatId) {
      this.previousChatId = currentChatId;
      this.previousMessageCount = 0;
      this.previousLastMessageId = undefined;
    }

    const currentMessageCount = this.session.messages.length;
    const lastMessage = currentMessageCount > 0
      ? this.session.messages[currentMessageCount - 1]
      : undefined;

    if (currentMessageCount < this.previousMessageCount) {
      this.previousMessageCount = currentMessageCount;
    }

    if (lastMessage && (currentMessageCount > this.previousMessageCount || lastMessage.id !== this.previousLastMessageId)) {
      if (lastMessage?.sender === 'user') {
        // Eine neue Frage heisst: der Nutzer ist wieder beim Geschehen – auch wenn er vorher
        // hochgescrollt hatte.
        this.stickToBottom = true;
        this.scrollToLastUserMessage();
      } else {
        this.scrollToLastUserMessage();
      }
      this.previousMessageCount = currentMessageCount;
      this.previousLastMessageId = lastMessage.id;
    }
  }

  /**
   * Die Schritt-Anzeige, aufeinanderfolgende Wiederholungen zusammengefasst.
   *
   * <p>Der Agent ruft dasselbe Werkzeug oft mehrfach auf -- die Dokumentensuche etwa einmal je
   * Suchbegriff, gemessen bis zu acht Mal in EINER Runde. Untereinander gelistet ergab das
   * achtmal dieselbe Zeile; hier steht sie einmal mit einem Zaehler.
   *
   * <p>Zusammengefasst wird nur, was <strong>direkt aufeinander folgt</strong>: eine globale
   * Gruppierung nach Beschriftung wuerde die Reihenfolge verfaelschen, sobald zwischen zwei
   * Aufrufen desselben Werkzeugs ein anderes liegt.
   *
   * <p>Der Zustand der Gruppe ist der ungünstigste ihrer Schritte: laeuft einer noch, laeuft die
   * Gruppe; ist einer gescheitert, ist sie gescheitert. Sonst stimmte das Häkchen, waehrend noch
   * gearbeitet wird.
   */
  get groupedToolSteps(): GroupedToolStep[] {
    const groups: GroupedToolStep[] = [];

    for (const step of this.session.toolSteps) {
      const last = groups[groups.length - 1];
      if (last && last.label === step.label) {
        last.count++;
        last.status = this.mergeStatus(last.status, step.status);
        // Die Dauer des jeweils letzten Schrittes ist die aussagekraeftigste: sie gehoert zum
        // Aufruf, auf den gerade gewartet wird.
        last.detail = step.detail;
        continue;
      }
      groups.push({
        key: step.stepId,
        label: step.label,
        status: step.status,
        detail: step.detail,
        count: 1,
      });
    }

    return groups;
  }

  private mergeStatus(current: ChatToolStep['status'],
                      incoming: ChatToolStep['status']): ChatToolStep['status'] {
    if (current === 'running' || incoming === 'running') return 'running';
    if (current === 'failed' || incoming === 'failed') return 'failed';
    return 'done';
  }

  ngOnInit() {
    if (!this.session.currentChat) {
      this.session.messages = [];
    }

    // Mit ngOnDestroy abgemeldet: die Ansicht wird beim Wechsel zwischen Quickstart und
    // klassischem Chat mehrfach auf- und abgebaut.
    this.scrollSubscription = this.session.messagesScrollDown.subscribe(() => {
      initFlowbite();
      this.followStreamingOutput();
    });
  }

  usePrompt(prompt: string) {
    this.session.question = prompt
  }

  get project() {
    return this.stateConfigService.getProject()
  }

  getProjectName(projectId: string) {
    let name = ''
    this.chatService.getProjects().forEach(project => {
      if (project.id === projectId) {
        name = project.name
      }
    })
    return name
  }

  scrollToBottom(): void {
    try {
      const scrollContainer = this.getScrollContainer();
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
        this.scheduleFollowUpScroll(() => {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        });
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  isCompactLayout(): boolean {
    return this.chatService.isFloatingMode && this.surface !== 'mycheck';
  }

  hasInlineUiBlocks(message: Message): boolean {
    return this.getInlineUiBlocks(message).length > 0;
  }

  hasUiBlocks(message: Message): boolean {
    return (message.uiBlock?.length ?? 0) > 0;
  }

  getInlineUiBlocks(message: Message): UiBlock[] {
    return (message.uiBlock ?? []).filter(uiBlock => uiBlock.type !== 'options' && uiBlock.type !== 'unavailable');
  }

  hasUnavailableUiBlocks(message: Message): boolean {
    return this.getUnavailableUiBlocks(message).length > 0;
  }

  getUnavailableUiBlocks(message: Message): UiBlock[] {
    return (message.uiBlock ?? []).filter(uiBlock => uiBlock.type === 'unavailable');
  }

  trackByUiBlock(index: number, uiBlock: UiBlock): string {
    const optionsKey = (uiBlock.options ?? []).map(option => option.value).join('|');
    const label = uiBlock.question ?? uiBlock.summary ?? uiBlock.unavailableText ?? '';
    return `${index}-${uiBlock.type}-${optionsKey}-${label}`;
  }

  getMycheckWelcomeParagraphs(message: Message): string[] {
    return this.splitMycheckWelcomeParagraphs(message);
  }

  getSourceFiles(message: Message): [] | { dataId: string, fileName: string, page: number }[] {
    const data = message.metadata?.['files']
    if (data) {
      let files = JSON.parse(data)
      files = files.filter((currentElement: any, index: any, array: any) => index === array.findIndex((findTest: any) =>
        findTest.dataId === currentElement.dataId
      ))
      return files
    }
    return []
  }

  protected readonly faTriangleExclamation = faTriangleExclamation;

  getSourceFilesNew(message: Message){
    let data = message.files
      if (data) {
        data = data.filter((currentElement: any, index: any, array: any) => index === array.findIndex((findTest: any) =>
          (currentElement.id && findTest.id === currentElement.id) ||
          (currentElement.dataId && findTest.dataId === currentElement.dataId)
        ))
      return data
    }
    return [];
  }

  openSource(source: { dataId: string, fileName: string, page: number }) {
    const id = source.dataId
    const url = "projects/" + this.project?.id + "/files/" + id
    this.popOutService.open(url, source.fileName, true)
  }

  /**
   * Lange Aufzählungen einklappen, sobald die Antwort steht. Während des Streamens nicht: dort
   * rendert jedes Delta neu, und eine Liste, die gerade wächst, würde ständig auf- und zuspringen.
   */
  onMarkdownReady(markdown: MarkdownComponent, message: Message): void {
    this.chatLinkService.markExternalLinks(markdown.element.nativeElement);
    if (this.isStreamingText(message)) return;
    collapseLongLists(markdown.element.nativeElement);
    addTableCsvExport(markdown.element.nativeElement);
  }

  /**
   * Endet ein Stream, ändert sich der Text oft nicht mehr – ngx-markdown rendert dann nicht neu und
   * meldet kein `ready`. Ohne diesen Nachzug bliebe ausgerechnet die frische Antwort ausgeklappt.
   */
  private collapseListsAfterStreaming(): void {
    const streaming = this.session.messages.some(message => this.isStreamingText(message));
    if (this.wasStreaming && !streaming) {
      this.chatViewContainer?.nativeElement
        ?.querySelectorAll('.chat-markdown')
        .forEach((element: HTMLElement) => {
          collapseLongLists(element);
          addTableCsvExport(element);
        });
    }
    this.wasStreaming = streaming;
  }

  /** Die jüngste Antwort – nur sie lässt sich neu generieren. */
  isLastBotMessage(index: number): boolean {
    const messages = this.session.messages;
    for (let j = messages.length - 1; j >= 0; j--) {
      if (messages[j].sender === 'bot') return j === index;
    }
    return false;
  }

  /** Ob der Verlauf überhaupt eine Antwort enthält – sonst steht die Review-Leiste am Ende der Liste. */
  hasBotMessage(): boolean {
    return this.session.messages.some(message => message.sender === 'bot');
  }

  /** Begrüßung und Quickstart-Start haben keine Frage, auf die neu geantwortet werden könnte. */
  isWelcomeOrBootstrap(message: Message): boolean {
    return this.isMycheckWelcomeMessage(message) || message.id.startsWith('local-') || message.id.startsWith('welcome');
  }

  /** Interne Links in Antworten (Bericht, Lauf, myCheck starten) über den Router statt Neuladen. */
  onMarkdownClick(event: MouseEvent): void {
    this.chatLinkService.handleClick(event);
  }
  // New method to handle different file structures
  openSourceFile(file: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }


    // Handle different file object structures
    let fileId = file.id || file.dataId;
    let fileName = file.name || file.fileName || 'Unknown File';

    if (!fileId) {
      console.error('No file ID found:', file);
      return;
    }

    const url = "projects/" + this.project?.id + "/files/" + fileId;
    this.popOutService.open(url, fileName, true);
  }

  isNewDate(index: number): boolean {
    if (index === 0) return true;
    const currentDate = this.session.messages[index].createdOn.toISOString().split('T')[0];
    const previousDate = this.session.messages[index - 1].createdOn.toISOString().split('T')[0];
    return currentDate !== previousDate;
  }

  showTooltip(targetElId: string, triggerElId:string) {
    const targetEl = document.getElementById(targetElId);
    const triggerEl = document.getElementById(triggerElId);
    if (targetEl && triggerEl) {
      const tooltip =  new Tooltip(targetEl, triggerEl, {triggerType: 'hover', placement:'top'}, {id: targetElId, override: true});
      tooltip.show()
    }
  }

  getPrompts(){
    return this.chatService.definedPrompts
  }

  getFloatingPrompts(){
    return this.chatService.contextSuggestedPrompts
  }


  getColumnTypes(columns:string[]){
    return new Array(columns?.length ?? 0).fill('text')
  }

  getColumnAlignments(columns:string[]){
    return new Array(columns?.length ?? 0).fill('start')
  }

  getColumnWidth(columns:string[]){
    let width = this.calculateColumnWidth(columns?.length ?? 0)
    return new Array(columns?.length ?? 0).fill(width+'%')
  }
  getUserTitle(): string {
    const user = this.chatService.getUser();
    if (!user) return '';

    let title = '';

    // Add salutation if it exists and is not 'NONE'
    if (user.salutation && user.salutation !== 'NONE') {
      title += user.salutation === 'HERR' ? 'Herr ' : 'Frau ';
    }

    // Add title if it exists and is not 'NONE'
    if (user.title && user.title !== 'NONE') {
      const titleMap: { [key: string]: string } = {
        'DR': 'Dr. ',
        'PROF': 'Prof. ',
        'PROFDR': 'Prof. Dr. '
      };
      title += titleMap[user.title] || '';
    }

    return title;
  }

  calculateColumnWidth(x: number): string {
    const widthPerItem = Math.floor((100 / x) * 2) / 2;
    return widthPerItem.toFixed(2);
  }

  getMarkdown(input?: string | null): string {
    return normalizeMarkdown(input ?? '');
  }

  protected readonly faX = faX;
  protected readonly faChevronRight = faChevronRight;
  protected readonly Array = Array;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faFile = faFile;
  protected readonly faCopy = faCopy;
  protected readonly document = document;
  protected readonly faChatbicAi = faChatbicAi;

  hovering: boolean[] = [];
  copiedMessageIndex: number | null = null;
  private copyFeedbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  protected readonly faCheck = faCheck;

  /**
   * Waechst der Text dieser Nachricht gerade?
   *
   * <p>Steuert die leichte Puls-Animation. Bewusst zusaetzlich an den Textinhalt geknuepft: solange
   * nur die Werkzeugschritte laufen und noch kein Wort da ist, gaebe es nichts zu animieren – ein
   * pulsierender Leerraum waere bloss Unruhe.
   */
  isStreamingText(message: Message): boolean {
    return this.isThinkingMessage(message) && !!message.message;
  }

  /**
   * Zeigt diese Nachricht gerade die Zwischenschritte des Agenten?
   *
   * <p>Genau fuer die Nachricht, die gerade entsteht – verglichen wird die Referenz, nicht die
   * Kennung: die wechselt beim Abschluss von der lokalen `streaming-…` auf die des Servers.
   */
  isThinkingMessage(message: Message): boolean {
    return this.session.isBotThinking && message === this.session.streamingMessage;
  }
  protected readonly faThumbsUp = faThumbsUp;
  protected readonly faThumbsDown = faThumbsDown;
  protected readonly faRedo = faRedo;

  getMessageSources(message: Message): { label: string; icon: IconDefinition }[] {
    const sources: { label: string; icon: IconDefinition }[] = [];
    const meta = message.metadata;
    if (!meta) return sources;

    // Bewusst KEIN Badge für Modul oder geöffnetes Objekt: das zeigt bereits der Kontext über dem
    // Eingabefeld, und unter jeder Antwort wiederholt („IFC-Viewer", „IFC-Viewer", …) sagt es nichts.
    // Unter der Antwort steht nur, was der Nutzer ausdrücklich als Kontext ausgewählt hat.

    // Checks – show type + date like the context selection badges
    if (meta['bimCheckId']) {
      const check = this.session.BIM_Check;
      const dateStr = check?.id === meta['bimCheckId'] && check?.checkDate
        ? ` ${new Date(check.checkDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}`
        : '';
      sources.push({label: `LOIplus-Check${dateStr}`, icon: faMemoCircleCheck});
    }
    if (meta['loiCheckId']) {
      const check = this.session.LOI_Check;
      const dateStr = check?.id === meta['loiCheckId'] && check?.checkDate
        ? ` ${new Date(check.checkDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}`
        : '';
      sources.push({label: `LOI-Check${dateStr}`, icon: faMemoCircleCheck});
    }
    if (meta['hoaiCheckId']) {
      const check = this.session.HOAI_Check;
      const dateStr = check?.id === meta['hoaiCheckId'] && check?.checkDate
        ? ` ${new Date(check.checkDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}`
        : '';
      sources.push({label: `HOAI-Check${dateStr}`, icon: faMemoCircleCheck});
    }
    if (meta['allRightCheckId']) {
      const check = this.session.QUALITY_Check;
      const dateStr = check?.id === meta['allRightCheckId'] && check?.checkDate
        ? ` ${new Date(check.checkDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}`
        : '';
      sources.push({label: `All-Right-Check${dateStr}`, icon: faBadgeCheck});
    }

    // Als Kontext gewählte myCheck-Prüfergebnisse.
    if (meta['checkRunNames']) {
      try {
        for (const name of JSON.parse(meta['checkRunNames']) as string[]) {
          sources.push({label: name, icon: faMemoCircleCheck});
        }
      } catch {
        // Unlesbare Metadaten älterer Nachrichten: dann eben ohne dieses Badge.
      }
    }

    // Files – show individual file names like the context selection. Namen kennt nur die laufende
    // Sitzung; im geladenen Verlauf stehen nur Kennungen. Die werden zu EINEM Badge „3 Dateien"
    // zusammengefasst statt dreimal „Datei" zu zeigen.
    if (meta['projectFileId']) {
      const fileIds: string[] = meta['projectFileId'].split(',').map((id: string) => id.trim()).filter((id: string) => !!id);
      let named: { id?: string; fileName?: string }[] = [];
      try {
        named = meta['projectFiles'] ? JSON.parse(meta['projectFiles']) : [];
      } catch {
        named = [];
      }
      let unnamed = 0;
      for (const fileId of fileIds) {
        const fileName = named.find(f => f.id === fileId)?.fileName
          ?? this.session.selectedFiles.find(f => f.id === fileId)?.fileName;
        if (fileName) {
          sources.push({label: fileName, icon: faFilePdf});
        } else {
          unnamed++;
        }
      }
      if (unnamed > 0) {
        sources.push({label: unnamed === 1 ? '1 Datei' : `${unnamed} Dateien`, icon: faFilePdf});
      }
    }

    return sources;
  }

  isContextEvent(message: Message): boolean {
    return message.metadata?.['eventType'] === 'context_files';
  }

  getWelcomeUploadLabel(message: Message): string {
    const option = (message.uiBlock ?? [])
      .flatMap(uiBlock => uiBlock.options ?? [])
      .find(currentOption => currentOption.value === 'upload_file');
    return option?.label || 'Datei hochladen';
  }

  shouldShowWelcomeUploadButton(message: Message): boolean {
    return this.isMycheckWelcomeMessage(message) && !this.hasUploadOption(message);
  }

  private scrollToLastUserMessage(): void {
    try {
      const scrollContainer = this.getScrollContainer();
      if (!scrollContainer) return;

      const lastUserMessage = this.getLastUserMessageForLatestBot();

      if (!lastUserMessage) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
        return;
      }

      this.updateQuestionAnchorSpacer(scrollContainer, lastUserMessage);
      this.scrollToElementTop(scrollContainer, lastUserMessage);
      this.scheduleFollowUpScroll(() => {
        this.updateQuestionAnchorSpacer(scrollContainer, lastUserMessage);
        this.scrollToElementTop(scrollContainer, lastUserMessage);
      });
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  private getLastUserMessageForLatestBot(): HTMLElement | undefined {
    const container = this.chatViewContainer?.nativeElement as HTMLElement | undefined;
    if (!container) return undefined;

    const renderedMessages = Array.from(
      container.querySelectorAll('[data-chatbic-message-role]')
    ) as HTMLElement[];

    let seenLatestBot = false;
    for (let index = renderedMessages.length - 1; index >= 0; index--) {
      const message = renderedMessages[index];
      const role = message.getAttribute('data-chatbic-message-role');
      if (!seenLatestBot && role === 'bot') {
        seenLatestBot = true;
        continue;
      }
      if (seenLatestBot && role === 'user') {
        return message;
      }
    }

    const userMessages = container.querySelectorAll('[data-chatbic-user-message="true"]');
    return userMessages?.[userMessages.length - 1] as HTMLElement | undefined;
  }

  private scrollToElementTop(scrollContainer: HTMLElement, element: HTMLElement): void {
    const targetScrollTop = this.getElementTopInScrollContainer(element, scrollContainer)
      - ChatViewComponent.QUESTION_ANCHOR_TOP_OFFSET_PX;
    const boundedScrollTop = Math.max(0, Math.min(targetScrollTop, scrollContainer.scrollHeight - scrollContainer.clientHeight));
    this.scrollToPosition(scrollContainer, boundedScrollTop);
  }

  private getElementTopInScrollContainer(element: HTMLElement, scrollContainer: HTMLElement): number {
    const containerRect = scrollContainer.getBoundingClientRect();
    const messageRect = element.getBoundingClientRect();
    return scrollContainer.scrollTop + (messageRect.top - containerRect.top);
  }

  private updateQuestionAnchorSpacer(scrollContainer: HTMLElement, lastUserMessage: HTMLElement): void {
    const spacer = this.questionAnchorSpacer?.nativeElement;
    if (!spacer) return;

    spacer.style.height = '0px';
    const contentAfterQuestion = spacer.getBoundingClientRect().top - lastUserMessage.getBoundingClientRect().top;
    const visibleHeightBelowOffset = scrollContainer.clientHeight - ChatViewComponent.QUESTION_ANCHOR_TOP_OFFSET_PX;
    spacer.style.height = `${Math.max(0, visibleHeightBelowOffset - contentAfterQuestion)}px`;
  }

  isMycheckWelcomeMessage(message: Message): boolean {
    const channelData = this.getChannelData(message);
    const showUploadButton = message.metadata?.['showUploadButton'] ?? channelData?.['showUploadButton'];
    return this.isTruthyFlag(showUploadButton) && this.getMessageMode(message, channelData) === 'mycheck';
  }

  private scrollToPosition(scrollContainer: HTMLElement, top: number): void {
    if (typeof scrollContainer.scrollTo === 'function') {
      try {
        scrollContainer.scrollTo({top, behavior: 'smooth'});
      } catch {
        scrollContainer.scrollTo(0, top);
      }
    }
    // Ensure position is applied even when smooth scrolling is not supported.
    scrollContainer.scrollTop = top;
  }

  private getScrollContainer(): HTMLElement | null {
    const container = this.chatViewContainer?.nativeElement as HTMLElement | undefined;
    if (!container) return null;

    const classBasedContainer = container.closest('.overflow-y-auto') as HTMLElement | null;
    if (classBasedContainer) return classBasedContainer;

    let parent = container.parentElement;
    while (parent) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }

    return document.scrollingElement as HTMLElement | null;
  }

  /**
   * Laeuft dem Stream hinterher.
   *
   * <p>Aufgerufen bei jedem Token. Waehrend des Streamens aendert sich weder die Anzahl der
   * Nachrichten noch ihre Kennung – die vorhandene Scroll-Logik in {@link ngAfterViewChecked}
   * greift deshalb nicht, sie reagiert nur auf NEUE Nachrichten. Ohne diese Methode stand die
   * Ansicht still, waehrend der Text unter dem sichtbaren Bereich weiterwuchs.
   */
  private followStreamingOutput(): void {
    this.attachScrollListener();
    if (!this.stickToBottom) return;

    const scrollContainer = this.getScrollContainer();
    if (!scrollContainer) return;
    const lastUserMessage = this.getLastUserMessageForLatestBot();
    if (lastUserMessage) {
      this.updateQuestionAnchorSpacer(scrollContainer, lastUserMessage);
    }
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }

  /**
   * Meldet den Scroll-Listener an – einmalig, sobald der Container aufloesbar ist.
   *
   * <p>Der Container steht beim Initialisieren noch nicht fest: er wird ueber die Elternkette
   * gesucht und haengt davon ab, ob der Chat schwebend oder ganzseitig gerendert wird.
   */
  private attachScrollListener(): void {
    const scrollContainer = this.getScrollContainer();
    if (!scrollContainer || scrollContainer === this.scrollListenerTarget) return;

    this.detachScrollListener();
    scrollContainer.addEventListener('scroll', this.onScrollContainerScrolled, {passive: true});
    this.scrollListenerTarget = scrollContainer;
  }

  private detachScrollListener(): void {
    this.scrollListenerTarget?.removeEventListener('scroll', this.onScrollContainerScrolled);
    this.scrollListenerTarget = undefined;
  }

  /**
   * Entscheidet nach jedem Scrollen neu, ob die Ansicht weiter mitlaufen soll.
   *
   * <p>Bewusst ueber den <em>Abstand zum Ende</em> und nicht darueber, wer gescrollt hat. Die
   * Unterscheidung „Nutzer oder Programm" ist im Browser nicht verlaesslich zu treffen; sie ist hier
   * aber auch gar nicht noetig: nach einem eigenen Sprung ans Ende ist der Abstand null, das
   * Mitlaufen bleibt also an. Nur wer wirklich hochscrollt, schaltet es ab.
   *
   * <p>Als Pfeilfunktion, damit die Referenz zum Abmelden stabil bleibt.
   */
  private readonly onScrollContainerScrolled = (): void => {
    const scrollContainer = this.scrollListenerTarget;
    if (!scrollContainer) return;

    const distanceToBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    this.stickToBottom = distanceToBottom <= ChatViewComponent.STICK_TO_BOTTOM_THRESHOLD_PX;
  };

  private scheduleFollowUpScroll(callback: () => void): void {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(callback);
    });
  }

  private splitMycheckWelcomeParagraphs(message: Message): string[] {
    const normalizedMessage = this.getMarkdown(message.message);
    return normalizedMessage
      .split(/\n\s*\n+/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0);
  }

  private getMessageMode(message: Message, channelData?: Record<string, unknown>): string {
    const metadataMode = message.metadata?.['mode'];
    if (metadataMode) return metadataMode;
    const channelDataMode = channelData?.['mode'];
    if (typeof channelDataMode === 'string') return channelDataMode;
    return this.surface;
  }

  private getChannelData(message: Message): Record<string, unknown> | undefined {
    const rawChannelData = message.metadata?.['channelData'] ?? message.metadata?.['channel_data'];
    if (!rawChannelData) return undefined;
    try {
      const parsed = JSON.parse(rawChannelData);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : undefined;
    } catch {
      return undefined;
    }
  }

  private isTruthyFlag(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
  }

  private hasUploadOption(message: Message): boolean {
    return (message.uiBlock ?? [])
      .some(uiBlock => (uiBlock.options ?? []).some(option => option.value === 'upload_file'));
  }

  getAnswerSources(botMessageIndex: number): { label: string; icon: IconDefinition }[] {
    // Walk backwards from the bot message to find the preceding user message
    for (let j = botMessageIndex - 1; j >= 0; j--) {
      const msg = this.session.messages[j];
      if (msg.sender === 'user') {
        return this.getMessageSources(msg);
      }
    }
    return [];
  }

  /** Nachrichtentext plus myCheck-Zusammenfassung als Markdown, siehe {@link messageCopyText}. */
  copyText(message: Message): string {
    return messageCopyText(message);
  }

  onMessageCopied(isCopied: boolean, messageIndex: number): void {
    if (!isCopied) {
      return;
    }

    this.copiedMessageIndex = messageIndex;

    if (this.copyFeedbackTimeoutId) {
      clearTimeout(this.copyFeedbackTimeoutId);
    }

    this.copyFeedbackTimeoutId = setTimeout(() => {
      this.copiedMessageIndex = null;
      this.copyFeedbackTimeoutId = null;
    }, 2000);
  }

  isMessageCopied(messageIndex: number): boolean {
    return this.copiedMessageIndex === messageIndex;
  }

  ngOnDestroy(): void {
    if (this.copyFeedbackTimeoutId) {
      clearTimeout(this.copyFeedbackTimeoutId);
    }
    this.scrollSubscription?.unsubscribe();
    this.detachScrollListener();
  }
}
